import { action, computed, makeObservable, observable } from "mobx";
import type { IPOW3Fetchers } from "../../../network/fetchers/POW3Fetchers";
import type { NewsItem } from "../../../network/domain/NewsItem";
import { getFromLocalStorageOrCookie, setInLocalStorageOrCookie } from "../../../util/local-storage/localStorageOrCookie";
import type { INewsItemMapper } from "./NewsItemMapper";

export interface INewsMenuStore {
	newsItems: NewsItem[];
	mostRecentNewsItemAlreadySeen: boolean;

	setLastShownNewsUpdateTimeInCookie(): void;
	loadNewsItems(): Promise<void>;
}

export class NewsMenuStore implements INewsMenuStore {
	private static readonly COOKIE_SHOWN_NEWS_NAME = "shownNews"; //Actually local storage, but was originally a cookie

	constructor(
		private readonly pow3Fetchers: IPOW3Fetchers,
		private readonly newsItemMapper: INewsItemMapper,
	) {
		makeObservable(this);
	}

	@observable
	public newsItems: NewsItem[] = [];

	@observable
	private _mostRecentNewsItemUpdatedTime = "";

	@action
	private setMostRecentNewsItemUpdatedTime(value: string) {
		this._mostRecentNewsItemUpdatedTime = value;
	}

	public async loadNewsItems(): Promise<void> {
		await this.getNewsItems();
	}

	@observable
	private _cookieWasSetToMostRecentUpdatedTime = false;

	@action
	private setNewsItems(newsItems: NewsItem[]) {
		this.newsItems = newsItems;
	}

	private async getNewsItems(): Promise<void> {
		const result = await this.pow3Fetchers.getNewsMenu();
		if (result.success && result.list.length > 0) {
			this.setNewsItems(result.list.map(n => this.newsItemMapper.map(n)));
			this.setMostRecentNewsItemUpdatedTime(result.list[0].updated);
		} else {
			this.setNewsItems([]);
		}
	}

	@computed
	public get mostRecentNewsItemAlreadySeen(): boolean {
		if (this.newsItems.length == 0 || this._cookieWasSetToMostRecentUpdatedTime) return true;
		return this.lastShownNewsUpdateTimeFromCookie == this._mostRecentNewsItemUpdatedTime;
	}

	private get lastShownNewsUpdateTimeFromCookie(): string | null {
		return getFromLocalStorageOrCookie(NewsMenuStore.COOKIE_SHOWN_NEWS_NAME);
	}

	@action
	public setLastShownNewsUpdateTimeInCookie(): void {
		setInLocalStorageOrCookie(NewsMenuStore.COOKIE_SHOWN_NEWS_NAME, this._mostRecentNewsItemUpdatedTime, 365);
		this._cookieWasSetToMostRecentUpdatedTime = true;
	}
}
